import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { extractIdFromSlug } from '../utils/slugUtils';
import { 
  BookOpenIcon, 
  AcademicCapIcon, 
  CalendarIcon,
  TagIcon,
  UserIcon,
  ArrowLeftIcon,
  PlayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import publicDeckService, { 
  type PublicDeckDetail as PublicDeckDetailType, 
  type DeckSeoMeta, 
  type PublicCard 
} from '../services/publicDeckService';
import { deckService } from '../services/deckService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { getFirstName } from '../utils/nameUtils';

const PublicDeckDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  
  const [deck, setDeck] = useState<PublicDeckDetailType | null>(null);
  const [cards, setCards] = useState<PublicCard[]>([]);
  const [seoData, setSeoData] = useState<DeckSeoMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    const fetchDeckData = async () => {
      if (!slug) return;
      
      const id = extractIdFromSlug(slug);
      
      setLoading(true);
      setError(null);
      
      try {
        const [deckData, cardsData, seoMetaData] = await Promise.all([
          publicDeckService.getDeckById(id),
          publicDeckService.getDeckCards(id),
          publicDeckService.getDeckSeoData(id)
        ]);
        
        setDeck(deckData);
        setCards(cardsData);
        setSeoData(seoMetaData);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load deck details');
      } finally {
        setLoading(false);
      }
    };

    fetchDeckData();
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleStartLearning = async () => {
    if (user && slug) {
      setCopying(true);
      try {
        // Extract ID from slug for the copy operation
        const id = extractIdFromSlug(slug);
        
        // Copy the deck to user's collection
        const copiedDeck = await deckService.copyDeck(id);
        
        toast.success(`Deck "${copiedDeck.name}" copiado com sucesso!`);
        
        // Redirect to study the copied deck
        navigate(`/study/${copiedDeck._id}`);
      } catch (error: any) {
        console.error('Error copying deck:', error);
        
        // Handle specific error cases
        if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
          toast.error('Limite de decks atingido. Faça upgrade para Premium.');
        } else if (error.response?.data?.code === 'COPY_CARD_LIMIT_REACHED') {
          toast.error('Deck tem muitos cards para usuários gratuitos. Faça upgrade para Premium.');
        } else if (error.response?.status === 404) {
          toast.error('Deck não encontrado ou não está mais disponível.');
        } else {
          toast.error('Erro ao copiar deck. Tente novamente.');
        }
      } finally {
        setCopying(false);
      }
    } else {
      // Redirect to register with study URL as redirect
      navigate(`/register?redirect=/study/${slug}`);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDark ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !deck || !seoData) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${
        isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Deck Não Encontrado</h1>
          <p className="text-gray-600 mb-6">
            {error || 'O deck que você está procurando não existe ou foi removido.'}
          </p>
          <Link
            to="/search"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Voltar à Busca
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* Enhanced Title & Description */}
        <title>{`${deck.name} - ${deck.cardCount} Flashcards | Flashcards Grátis | Upmindy`}</title>
        <meta name="description" content={seoData.description} />
        
        {/* Keywords & SEO */}
        <meta name="keywords" content={seoData.keywords.join(', ')} />
        <meta name="author" content={seoData.authorName || 'Upmindy'} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta httpEquiv="Content-Language" content="pt-BR" />
        <meta name="theme-color" content="#3B82F6" />
        
        {/* Canonical URLs */}
        <link rel="canonical" href={seoData.canonicalUrl} />
        <link rel="alternate" hrefLang="pt-BR" href={seoData.canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={seoData.canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={`${deck.name} - Flashcards | Upmindy`} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Upmindy" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={deck.createdAt} />
        <meta property="article:modified_time" content={deck.updatedAt} />
        <meta property="article:author" content={deck.creatorName || 'Upmindy'} />
        <meta property="article:section" content="Education" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@upmindy_app" />
        <meta name="twitter:creator" content="@upmindy_app" />
        <meta name="twitter:title" content={`${deck.name} - Flashcards | Upmindy`} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.ogImage} />
        <meta name="twitter:image:alt" content={`Flashcards: ${deck.name}`} />
        <meta name="twitter:label1" content="Cards" />
        <meta name="twitter:data1" content={`${deck.cardCount} flashcards`} />
        <meta name="twitter:label2" content="Tipo" />
        <meta name="twitter:data2" content="Flashcards" />
        
        {/* Structured Data - LearningResource Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["Course", "LearningResource"],
            "name": deck.name,
            "description": seoData.description,
            "educationalLevel": "intermediate",
            "inLanguage": "pt-BR",
            "learningResourceType": "Flashcard Set",
            "isAccessibleForFree": true,
            "timeRequired": `PT${Math.ceil(deck.cardCount * 1.5)}M`,
            "creator": {
              "@type": "Person",
              "name": deck.creatorName || "Upmindy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Upmindy",
              "url": "https://upmindy.com"
            },
            "datePublished": deck.createdAt,
            "dateModified": deck.updatedAt,
            "url": seoData.canonicalUrl,
            "keywords": seoData.keywords.join(', ')
          })}
        </script>
        
        {/* Structured Data - Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Início",
                "item": "https://upmindy.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Explorar",
                "item": "https://upmindy.com/search"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": deck.name,
                "item": seoData.canonicalUrl
              }
            ]
          })}
        </script>
        
        {/* Structured Data - FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Quantos cards tem o deck ${deck.name}?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Este deck contém ${deck.cardCount} flashcards cuidadosamente elaborados.`
                }
              },
              {
                "@type": "Question",
                "name": "Posso estudar este deck gratuitamente?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Sim! Este deck está disponível gratuitamente no Upmindy."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        {/* Header */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Breadcrumbs */}
            <nav className="flex mb-6" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link
                    to="/"
                    className={`inline-flex items-center text-sm font-medium ${
                      isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Início
                  </Link>
                </li>
                <li>
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <Link
                      to="/search"
                      className={`ml-1 text-sm font-medium md:ml-2 ${
                        isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Explorar
                    </Link>
                  </div>
                </li>
                <li aria-current="page">
                  <div className="flex items-center">
                    <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className={`ml-1 text-sm font-medium md:ml-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {deck.name.length > 30 ? `${deck.name.substring(0, 30)}...` : deck.name}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>

            {/* Deck Header */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <BookOpenIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-bold mb-2">{deck.name}</h1>
                <p className={`text-lg mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {deck.description || 'Nenhuma descrição disponível'}
                </p>
                
                {/* Card Count Badge */}
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => {
                      const cardsSection = document.getElementById('cards-preview');
                      if (cardsSection) {
                        cardsSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className={`inline-flex items-center px-4 py-2 rounded-full transition-colors hover:scale-105 transform duration-200 ${
                      isDark 
                        ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    <AcademicCapIcon className="h-5 w-5 mr-2" />
                    <span className="font-semibold">{deck.cardCount} Cards</span>
                    <EyeIcon className="h-4 w-4 ml-2" />
                  </button>
                </div>
                
                {/* Tags */}
                {deck.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {deck.tags.map(tag => (
                      <span
                        key={tag}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                          isDark 
                            ? 'bg-gray-700 text-gray-300' 
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <TagIcon className="h-3 w-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={handleStartLearning}
                  disabled={copying}
                  className={`flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg transition-colors font-medium ${
                    copying 
                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copying ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Copiando...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-5 w-5 mr-2" />
                      {user ? 'Começar a Aprender' : 'Entrar para Aprender'}
                    </>
                  )}
                </button>
                
                <Link
                  to="/search"
                  className={`flex-1 inline-flex items-center justify-center px-6 py-3 rounded-lg border transition-colors font-medium ${
                    isDark 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  Explorar Mais Decks
                </Link>
              </div>

              {/* Cards Preview */}
              {cards.length > 0 && (
                <div id="cards-preview" className={`p-6 rounded-lg mt-8 ${
                  isDark ? 'bg-gray-800' : 'bg-white'
                } shadow-sm border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className="text-lg font-semibold mb-4">Prévia dos Cards ({cards.length})</h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {cards.map((card, index) => (
                      <div key={card._id} className={`p-4 rounded-lg border ${
                        isDark ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="space-y-3">
                          {/* Card number */}
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}>
                              Card #{index + 1}
                            </span>
                            <span className={`text-xs ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Dificuldade: {card.difficulty}/5
                            </span>
                          </div>
                          
                          {/* Front side */}
                          <div className={`p-3 rounded-lg ${
                            isDark ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'
                          }`}>
                            <h4 className={`text-sm font-semibold mb-2 ${
                              isDark ? 'text-blue-300' : 'text-blue-800'
                            }`}>Frente</h4>
                            <p className={`text-sm ${
                              isDark ? 'text-gray-200' : 'text-gray-700'
                            }`} dangerouslySetInnerHTML={{ __html: card.front }} />
                          </div>
                          
                          {/* Back side */}
                          <div className={`p-3 rounded-lg ${
                            isDark ? 'bg-emerald-900/20 border border-emerald-800/30' : 'bg-emerald-50 border border-emerald-200'
                          }`}>
                            <h4 className={`text-sm font-semibold mb-2 ${
                              isDark ? 'text-emerald-300' : 'text-emerald-800'
                            }`}>Verso</h4>
                            <p className={`text-sm ${
                              isDark ? 'text-gray-200' : 'text-gray-700'
                            }`} dangerouslySetInnerHTML={{ __html: card.back }} />
                          </div>
                          
                          {/* Tags if any */}
                          {card.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {card.tags.map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                                    isDark 
                                      ? 'bg-gray-700 text-gray-300' 
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className={`p-6 rounded-lg ${
                isDark ? 'bg-gray-800' : 'bg-white'
              } shadow-sm border ${isDark ? 'border-gray-700' : 'border-gray-200'} sticky top-8`}>
                <h3 className="text-lg font-semibold mb-4">Informações do Flashcard</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <UserIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Criado por
                      </p>
                      <p className="font-medium">{getFirstName(deck.creatorName)}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <CalendarIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Criado em
                      </p>
                      <p className="font-medium">{formatDate(deck.createdAt)}</p>
                    </div>
                  </div>

                  {deck.lastStudied && (
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-500 mr-3" />
                      <div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Última Atualização
                        </p>
                        <p className="font-medium">{formatDate(deck.updatedAt)}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <AcademicCapIcon className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Dificuldade
                      </p>
                      <p className="font-medium">
                        {deck.cardCount < 20 ? 'Iniciante' : 
                         deck.cardCount < 50 ? 'Intermediário' : 'Avançado'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleStartLearning}
                    disabled={copying}
                    className={`w-full py-3 px-4 rounded-lg transition-colors font-medium ${
                      copying 
                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {copying ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Copiando...
                      </div>
                    ) : (
                      user ? 'Começar a Aprender Agora' : 'Entrar para Começar'
                    )}
                  </button>
                  {!user && (
                    <p className={`text-xs text-center mt-2 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Conta gratuita necessária
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicDeckDetail;